import React, { useState, useEffect } from 'react';
import { Search, Flag, Check, X, Eye, Star } from 'lucide-react';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setTimeout(() => {
      setReviews([
        {
          id: 1,
          customerName: 'Jana Svobodová',
          providerName: 'Praha Cleaning Service',
          service: 'Apartment Cleaning',
          rating: 5,
          comment: 'Excellent service! Very professional and thorough cleaning.',
          date: '2025-01-09',
          status: 'approved',
          flagged: false,
          flagReason: null
        },
        {
          id: 2,
          customerName: 'Petr Novák',
          providerName: 'Brno Plumbing Masters',
          service: 'Pipe Repair',
          rating: 2,
          comment: 'Service was delayed and the plumber was rude. Very disappointed.',
          date: '2025-01-08',
          status: 'flagged',
          flagged: true,
          flagReason: 'Inappropriate language reported'
        },
        {
          id: 3,
          customerName: 'Marie Černá',
          providerName: 'Ostrava Electric Solutions',
          service: 'Electrical Installation',
          rating: 4,
          comment: 'Good work overall, but could have been faster.',
          date: '2025-01-07',
          status: 'pending',
          flagged: false,
          flagReason: null
        }
      ]);
      setLoading(false);
    }, 1000);
  };

  const handleStatusChange = async (reviewId, newStatus) => {
    setReviews(reviews.map(review =>
      review.id === reviewId
        ? { ...review, status: newStatus, flagged: newStatus === 'flagged' }
        : review
    ));
  };

  const filteredReviews = reviews.filter(review => {
    const matchesSearch = review.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         review.providerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         review.service.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || review.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const StatusBadge = ({ status, flagged }) => {
    if (flagged) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Flagged</span>;
    }
    
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const RatingStars = ({ rating }) => (
    <div className="flex items-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
        />
      ))}
      <span className="ml-2 text-sm text-gray-600">{rating}/5</span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Review Management</h1>
          <p className="text-gray-600 mt-1">Monitor and moderate customer reviews</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {reviews.filter(r => r.flagged).length} flagged reviews
          </div>
          <div className="text-sm text-gray-500">
            {filteredReviews.length} total reviews
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search reviews..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Reviews</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="flagged">Flagged</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {loading ? (
          <div className="card flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          filteredReviews.map((review) => (
            <div key={review.id} className={`card ${review.flagged ? 'border-red-200 bg-red-50' : ''}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">{review.customerName}</h3>
                      <p className="text-sm text-gray-600">{review.providerName} - {review.service}</p>
                      <p className="text-xs text-gray-500 mt-1">{review.date}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <StatusBadge status={review.status} flagged={review.flagged} />
                      {review.flagged && (
                        <Flag className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </div>
                  
                  <RatingStars rating={review.rating} />
                  
                  <div className="mt-3">
                    <p className="text-gray-800">{review.comment}</p>
                    {review.flagReason && (
                      <div className="mt-2 p-2 bg-red-100 rounded-lg">
                        <p className="text-sm text-red-800">
                          <strong>Flag reason:</strong> {review.flagReason}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mt-4 lg:mt-0 lg:ml-6 flex items-center space-x-2">
                  <button
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  
                  {review.status === 'pending' || review.status === 'flagged' ? (
                    <>
                      <button
                        onClick={() => handleStatusChange(review.id, 'approved')}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                        title="Approve"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleStatusChange(review.id, 'rejected')}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Reject"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleStatusChange(review.id, 'flagged')}
                      className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                      title="Flag Review"
                    >
                      <Flag className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Reviews;